<template>
  <InputBlock
    v-if="props.event?.general?.ticketing?.hasPromoCodesEnabled"
    :error="validationErrorMessage"
  >
    <div
      v-if="usedPromoCodes.length"
      class="w-full flex flex-col gap-2 mt-2 mb-1"
    >
      <div
        v-for="promoCode in usedPromoCodes"
        :key="promoCode"
        class="flex items-center justify-between gap-1"
      >
        <Icon
          name="tag-02"
          class="size-4 text-gray-400"
        />
        <div class="text-sm text-gray-500 flex-1">
          {{ props.section.$t('poly.promoCodeUsed') }} ({{ promoCode.code }})
        </div>
        <div class="text-sm text-gray-500">
          -{{ useFormatPrice(promoCode.amount, props.event?.general?.ticketing?.currency) }}
        </div>
      </div>
    </div>
    <div
      v-if="canAddMorePromoCodes"
      class="w-full"
    >
      <button
        class="text-sm font-medium text-(fg-accent) cursor-pointer"
        type="button"
        data-cy="promo-code-add-button"
        @click="toggleIsOpen"
      >
        {{ props.section.$t('poly.promoCodeAdd') }}
      </button>

      <el-collapse-transition>
        <div v-show="isOpen">
          <div class="flex items-center gap-2">
            <TextInput
              ref="inputRef"
              data-cy="promo-code-input"
              :model-value="promoCodeValue"
              :placeholder="props.section.$t('poly.promoCodeEnter')"
              @keydown.enter.prevent="applyPromoCode"
              @update:model-value="(value) => promoCodeValue = value.replace(/\s/g, '').toUpperCase()"
            />

            <Button
              type="regular-outline"
              native-type="button"
              :loading="isPromoCodeApplying"
              :disabled="!promoCodeValue.length"
              data-cy="promo-code-apply-button"
              @click="applyPromoCode"
            >
              {{ props.section.$t('poly.promoCodeApply') }}
            </Button>
          </div>
        </div>
      </el-collapse-transition>
    </div>
  </InputBlock>
</template>

<script setup>
const props = defineProps({
  section: {
    type: Object,
    required: true,
  },
  form: {
    type: Object,
    required: true,
  },
  event: {
    type: Object,
    required: true,
  },
  ticketsTotalQuantity: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['set-promo-codes']);

// Refs
const isOpen = ref(false);
const promoCodeValue = ref('');
const isPromoCodeApplying = ref(false);
const validationErrorMessage = ref(null);

// Template Refs
const inputRef = useTemplateRef('inputRef');

// Computed
const usedPromoCodes = computed(() => props.form?.extras?.payment?.usedPromoCodes || []);

const canAddMorePromoCodes = computed(() => props.ticketsTotalQuantity > usedPromoCodes.value.length);

// Methods
const toggleIsOpen = () => {
  isOpen.value = !isOpen.value;

  if (isOpen.value) {
    nextTick(() => {
      inputRef.value?.inputRef?.input?.focus?.();
    });
  }
};

const applyPromoCode = async() => {
  isPromoCodeApplying.value = true;
  validationErrorMessage.value = null;

  const response = await useApi.post(`/public/events/${props.event?._id}/verify-promo-code`, {
    body: {
      code: promoCodeValue.value,
      quantity: usedPromoCodes.value?.filter((promoCode) => promoCode.code === promoCodeValue.value)?.length + 1 || 1,
    },
  }).catch((error) => {
    if (error?.statusCode === 400) {
      validationErrorMessage.value = props.section.$t('poly.promoCodeInvalid');
    } else {
      useToast.error(props.section.$t('poly.errorLoadingDataRefresh'));
    }
    return false;
  });

  if (response) {
    emit('set-promo-codes', [
      ...usedPromoCodes.value,
      {
        id: response.id,
        code: promoCodeValue.value,
        amount: response.amount,
      },
    ]);

    promoCodeValue.value = '';
    isOpen.value = false;
  }

  isPromoCodeApplying.value = false;
};
</script>
